<template>
  <c-input-container
    v-bind="$attrs"
    class="c-multi-checkbox control"
    :id="dataTestId"
  >
    <div class="inner" :style="rows">
      <checkbox-fragment
        v-for="(option, index) in options"
        :key="index"
        class="option"
        :label="option[displayBy]"
        :value="isSelected(option)"
        :disabled="!!option.disabled || disabled"
        :tooltip="option.tooltip"
        @input="handleInput(option, $event)"
        @mouseenter="onMouseEnter(option)"
        @mouseleave="onMouseLeave(option)"
      />
    </div>

    <div v-if="nativeCompatible" class="native">
      <input
        v-for="(option, index) in options"
        :key="index"
        type="checkbox"
        :name="name"
        :value="trackedOption(option)"
        :checked="isSelected(option)"
        @mouseenter="onMouseEnter(option)"
        @mouseleave="onMouseLeave(option)"
      >
    </div>

    <c-multi-select-item-list
      v-if="showSelectList"
      :items="itemsList"
      :display-by="displayBy"
      @remove="removeOption($event.event)"
    />
  </c-input-container>
</template>

<script>
import { defineAsyncComponent } from 'vue'

import CInputContainer from '@convenia/components/CInputContainer'
import CheckboxFragment from './fragments/Checkbox'
import props from './props'

/**
 * A multi checkbox component. When some option(s) is (are) selected, it emits an 'input'
 * event with the chosen option(s).
 */
export default {
  name: 'CMultiCheckboxBase',

  components: {
    CInputContainer,
    CheckboxFragment,
    CMultiSelectItemList: defineAsyncComponent(() => import('@convenia/components/CMultiSelectItemList/index.vue')),
  },

  props,

  computed: {
    dataTestId () {
      const fieldName = this.$attrs?.fieldName || this.name
      return `multi-checkbox-${fieldName}`
    },

    rows () {
      return 'grid-template-rows: repeat(' + Math.ceil(this.options.length / this.columns) + ', 1fr);'
    },

    optionsReference () {
      return this.options.reduce((optionsReference, option) => ({
        ...optionsReference,
        [option[this.trackBy]]: option
      }), {})
    },

    selectionSet () {
      const trackedValues = this.bindTrackBy
        ? this.value
        : this.value.reduce((validValues, selection) => {
          const trackedValue = (selection || {})[this.trackBy]
          return [ ...validValues, ...(trackedValue ? [ trackedValue ] : []) ]
        }, [])

      return new Set(trackedValues)
    },

    itemsList () {
      return [ ...this.selectionSet ].map(selection => this.optionsReference[selection])
    },
  },

  methods: {
    isSelected (option) {
      return this.selectionSet.has(option[this.trackBy])
    },

    removeOption (option) {
      const toRemoveTracking = item => this.bindTrackBy
        ? item
        : item[this.trackBy]

      const newValue = this.value.filter(item => toRemoveTracking(item) !== option[this.trackBy])
      this.$emit('input', newValue)
    },

    addOption (option) {
      const newItem = this.bindTrackBy ? option[this.trackBy] : option
      const newValue = [ ...this.value, newItem ]
      this.$emit('input', newValue)
    },

    trackedOption (option) {
      return this.bindTrackBy ? option[this.trackBy] : option
    },

    handleInput (option, event) {
      if (event) return this.addOption(option)
      this.removeOption(option)
    },

    onMouseEnter (option) {
      if (!option.hint) return

      this.$emit('hint:show', option.hint)
    },

    onMouseLeave (option) {
      if (!option.hint) return

      this.$emit('hint:hide')
    }
  },

  watch: {
    bindTrackBy () {
      this.$emit('input', [])
    }
  }
}
</script>

<style lang="scss">
.c-multi-checkbox {
  & > .inner {
    justify-content: space-around;

    display: grid;
    grid-column-gap: 30px;
    grid-auto-columns: 1fr;
    grid-auto-flow: column;

    grid-row-gap: 10px;

    & > .option {
      display: flex;
      flex-flow: row;
      align-items: center;
      justify-content: flex-start;

      & > .label {
        margin-left: 15px;
        @include responsive (tablet, desktop) { margin-left: 20px; }

        font-size: 14px;
      }
    }
  }

  & > .native {
    position: absolute;
    visibility: hidden;
  }

  & > .jumbo-validation { display: none; }
}
</style>
